import { useQuery } from 'react-query';
import { GET_SELF_ONBOARDING_DATA, formInitialData } from '../constants';
import { AppError } from 'utils/AppError';
import api from 'api';
import { parseGraphqlErrorFromExtensions } from 'utils/Api';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { useToast } from '@razorpay/blade/components';
import { FormFields } from '../types';

export const useSavedEmployeeData = () => {
  const loggedInUserId = useSelector(loggedInUserSelectors.peopleId);
  const toast = useToast();
  const selfOnboardingQueryResponse = useQuery({
    queryKey: [GET_SELF_ONBOARDING_DATA, loggedInUserId],
    queryFn: () => api.employeeOnboardingGql.queries.getSelfOnboardingData({ id: loggedInUserId! }),
    onError: (error: typeof AppError) => {
      toast.show({
        content: parseGraphqlErrorFromExtensions(error),
        color: 'negative',
      });
      window.location.href = '/dashboard';
    },
    select: (data) => {
      const basicDetails = data?.getSelfOnboardingData?.data?.basicDetails;
      const compensationDetails = data?.getSelfOnboardingData?.data?.compensationDetails;
      const personalDetails = data?.getSelfOnboardingData?.data?.personalDetails;
      const complianceDetails = data?.getSelfOnboardingData?.data?.complianceDetails;
      const userStatutoryDetails = data?.getSelfOnboardingData?.data?.userStatutoryDetails;

      return {
        basicDetails,
        compensationDetails,
        personalDetails,
        complianceDetails,
        userStatutoryDetails,
      };
    },
    refetchOnMount: false,
  });

  const complianceSettings = {
    isTdsEnabled: !!selfOnboardingQueryResponse.data?.complianceDetails.isTds,
    isEsicEnabled: !!selfOnboardingQueryResponse.data?.complianceDetails.isEsi,
    esicIpNumber: selfOnboardingQueryResponse.data?.userStatutoryDetails.esicIp,
    isPfEnabled: !!selfOnboardingQueryResponse.data?.complianceDetails.isPf,
    pfUan: selfOnboardingQueryResponse.data?.userStatutoryDetails.pfUan,
    pan: selfOnboardingQueryResponse.data?.userStatutoryDetails.panNumber ?? '',
  };

  const employeeDetails = {
    personalDetails: selfOnboardingQueryResponse.data?.personalDetails,
    compensationDetails: selfOnboardingQueryResponse.data?.compensationDetails,
    basicDetails: selfOnboardingQueryResponse.data?.basicDetails,
    complianceDetails: complianceSettings,
  };

  const personalDetailsResponse = selfOnboardingQueryResponse.data?.personalDetails || {};

  const dobValue = personalDetailsResponse?.dob;

  const maritalStatusValue = personalDetailsResponse?.maritalStatus;
  const genderValue = personalDetailsResponse?.gender;
  const marriageAnniversaryValue = personalDetailsResponse?.marriageAnniversary;

  const selfOnboardingFormInitialData: FormFields = {
    ...formInitialData,

    ...complianceSettings,
    ...personalDetailsResponse,
    dob: dobValue ? new Date(dobValue as string) : null,
    maritalStatus: maritalStatusValue ? [maritalStatusValue as string] : [],
    gender: genderValue ? [genderValue as string] : [],
    marriageAnniversary: marriageAnniversaryValue
      ? new Date(marriageAnniversaryValue as string)
      : null,
  };

  const shouldShowStatutoryDetailsStep = Boolean(
    complianceSettings?.isEsicEnabled || complianceSettings?.isPfEnabled,
  );

  return {
    employeeDetails,
    selfOnboardingFormInitialData,
    isLoadingQuery: selfOnboardingQueryResponse.isLoading || selfOnboardingQueryResponse.isFetching,
    shouldShowStatutoryDetailsStep,
  };
};
